<template>
  <div
    ref="buttonDropDown"
    :class="containerClasses"
  >
    <menu-wrapper 
      :class="menuWrapperClasses" 
      :disabled="isDisabled" 
      :max-height="effectiveMaxHeight"
    >
      <template #menu-button="{toggleMenu}">
        <button
          :class="primaryButtonClasses"
          :disabled="isDisabled"
          @click.prevent="toggleMenu"
        >
          <div v-if="showLeftIcon" :class="leftIconClasses">&nbsp;</div>
          <div v-if="showText" :class="textClasses">
            {{ text }}
          </div>
        </button>
        <div class="button-line"></div>

        <button
          :class="widgetMoreClasses"
          :disabled="isDisabled"
          @click.prevent="toggleMenu"
        ></button>
      </template>
      <template #menu-content>
        <menu-item
          v-for="(item, index) in menuItems"
          :key="index"
          :seperator-bottom="item.seperatorBottom"
          :disabled-item="item.disabledItem"
          :class="item.class"
          :icon="item.icon"
          @menu-item-click="clickItem(item)"
        >
          <v-menu v-if="item.IsSubMenu" placement="right" :offset-x="true">
            <div class="flex-between flex1">
              <div class="content">{{ item.text }}</div>
              <div :class="['icon24 icon right', item.icon]"></div>
            </div>
            <template #popper>
              <menu-item v-for="(itemSub, index) in item.subItem" @menu-item-click="clickItem(itemSub)">{{
                itemSub.text
              }}</menu-item>
            </template>
          </v-menu>
          <div v-else>
            {{ item.text }}
          </div>
        </menu-item>
      </template>
    </menu-wrapper>
  </div>
</template>
<script>
export default {
  name: 'MsDropDownButton'
}
</script>

<script setup>
import { ref } from 'vue'
import MenuWrapper from '@/components/dropdown/MenuWrapper.vue'
import MenuItem from '@/components/dropdown/MenuItem.vue'

// Import composables
import { useDropDownButton } from './dropdown-button/composables/useDropDownButton.js'
import { useDropDownButtonEvents } from './dropdown-button/composables/useDropDownButtonEvents.js'
import { useDropDownButtonStyles } from './dropdown-button/composables/useDropDownButtonStyles.js'
import { DEFAULT_PROPS, VALID_DROPDOWN_BUTTON_TYPES, EVENTS } from './dropdown-button/constants/dropdown-button-constants.js'

/**
 * MsDropDownButton Component - Refactored từ code gốc giữ nguyên logic
 */

/**
 * Component props definition - giữ nguyên các props gốc
 */
const props = defineProps({
  // Text hiển thị
  text: {
    defualt: null, // Giữ nguyên typo từ code gốc
    type: String,
  },
  // Icon trái
  leftIcon: {
    default: DEFAULT_PROPS.leftIcon,
    type: String,
  },
  // Icon phải
  rightIcon: {
    default: DEFAULT_PROPS.rightIcon,
    type: String,
  },
  // Loại button gồm 3 loại primary/secondary/third/four để hiện thị style tương ứng
  type: {
    default: DEFAULT_PROPS.type,
    type: String,
    validator: (value) => VALID_DROPDOWN_BUTTON_TYPES.includes(value)
  },
  /**
   * Trạng thái button disabled
   */
  disabled: {
    default: DEFAULT_PROPS.disabled,
    type: Boolean,
  },
  // chiều cao tối đa cho menu để hiển thị scroll nếu menu quá dài
  maxHeight: {
    default: DEFAULT_PROPS.maxHeight,
    type: Number,
  },
  /**
   * Danh sách các item của context menu
   */
  menuItems: {
    type: Array,
    default: () => DEFAULT_PROPS.menuItems,
  },
  /**
   * ĐVThi 18/02/2020
   * muốn đặt thêm stype cho lớp mục đích có cách để trên form tìm được đến control
   */
  classCustom: {
    default: DEFAULT_PROPS.classCustom,
    type: String,
  },
  /**
   * NNLAM 26/02/2020
   * có cho phép tự custom vị trí dropdown menu hay không
   */
  customPositionMenu: {
    type: Boolean,
    default: DEFAULT_PROPS.customPositionMenu,
  },
  /**
   * NNLAM 26/02/2020
   * Set lại chiều dài menu dropdown
   */
  customWidth: {
    type: Number,
    default: DEFAULT_PROPS.customWidth,
  },
  /**
   * NNLAM 26/02/2020
   * khoảng cách của button so với lề trên/dưới
   */
  heightOffset: {
    type: Number,
    default: DEFAULT_PROPS.heightOffset,
  },
  /**
   * NNLAM 26/02/2021
   * khoảng cách của button so với lề trái/phải
   */
  widthOffset: {
    type: Number,
    default: DEFAULT_PROPS.widthOffset,
  },
  /**
   * NNLAM 26/02/2021
   * Có show menu khi click vào icon right của button không, mặc định có
   */
  hasShowMenu: {
    type: Boolean,
    default: DEFAULT_PROPS.hasShowMenu,
  },
  /**
   * NNLAM 26/02/2021
   * Có tooltip cho btt không
   */
  tooltipText: {
    type: [String, Object],
    default: DEFAULT_PROPS.tooltipText,
  },
  // border hiển thị cho các button con
  hasBorder: {
    default: DEFAULT_PROPS.hasBorder,
    type: Boolean,
  },
  // border bên ngoài 2 button con
  hasBorderParent: {
    default: DEFAULT_PROPS.hasBorderParent,
    type: Boolean,
  },
  // button tiện ích footer có hover màu xanh nước biển
  isBtnFooterHelpFul: {
    default: DEFAULT_PROPS.isBtnFooterHelpFul,
    type: Boolean,
  },
  /**
   * Thêm class root để style menu-wrapper
   * tbngoc 19.07.2022
   */
  classRoot: {
    default: DEFAULT_PROPS.classRoot,
    type: String,
  },
})

/**
 * Component events definition
 */
const emit = defineEmits([EVENTS.MENU_CLICK])

/**
 * Use composables for modular functionality
 */

// Core dropdown button logic
const componentState = useDropDownButton(props)
const {
  isDisabled,
  hasText,
  hasLeftIcon,
  hasRightIcon,
  hasMenuItems,
  effectiveMaxHeight,
  isValidType,
  shouldShowBorder,
  shouldShowParentBorder,
  isFooterHelpful,
  shouldShowMenu,
  hasCustomPosition,
  customMenuWidth,
  menuHeightOffset,
  menuWidthOffset,
  hasTooltip
} = componentState

// Event handling - giữ nguyên logic gốc
const { clickItem } = useDropDownButtonEvents(props, emit)

// Styling logic - sử dụng computed variables thay vì hard-code
const {
  containerClasses,
  menuWrapperClasses,
  primaryButtonClasses,
  widgetMoreClasses,
  leftIconClasses,
  textClasses,
  showLeftIcon,
  showText
} = useDropDownButtonStyles(props, componentState)

// Giữ nguyên toggleMenu từ code gốc
const toggleMenu = ref(false)

// Expose component API - để tương thích với testing và usage
defineExpose({
  // Giữ nguyên interface gốc
  clickItem,
  toggleMenu,
  
  // Component state
  isDisabled,
  hasText,
  hasLeftIcon,
  hasRightIcon,
  hasMenuItems,
  effectiveMaxHeight,
  
  // Validation
  isValidType,
  
  // Styling
  containerClasses,
  showLeftIcon,
  showText
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/components/MsButton.scss';

:deep(.btn) {
  min-width: 60px;
}

.drop-down-icon {
  width: 36px;
  height: 24px;
  margin: 0 !important;
  .icon {
    margin: 0;
    cursor: pointer;
  }
}

:deep(.menu-button-container) {
  display: flex;
}
:deep(.flex) {
  display: flex;
}
:deep(.wrapper-utilities) {
  z-index: 1000 !important;
}
.context-item {
  height: 36px;
  padding: 2px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  .context-text {
    padding: 0 8px;
  }
  &:hover {
    cursor: pointer;
    background: $hover-bg;
    color: #14aa8a;
  }
}
.context-icon {
  margin-right: 8px;
}
.color-red {
  color: #ff0000;
}
.button-line {
  display: inline-block;
  width: 1px;
  height: 16px;
  background-color: #fff;
}
.drop-down-button {
  &.border-drop-down-btn {
    border: 1px solid #afafaf;
    border-radius: 3px;
  }
  button {
    &.primary-button {
      border-end-end-radius: unset;
      border-start-end-radius: unset;
    }
    &:focus-visible {
        z-index: 1;
      }
  }
}
</style>
